/**
 * Global Error Handler
 * Catches:
 * 1. JavaScript runtime errors (window.onerror)
 * 2. Unhandled promise rejections (unhandledrejection)
 * 3. API failures (via axios interceptor)
 */

import * as Sentry from '@sentry/react';
import { ErrorInfo } from '@/types/error';
import { getModuleName } from '@/utils/errorUtils';

// Helper to parse stack trace
export const parseStackTrace = (stack?: string) => {
	if (!stack) return undefined;
	const lines = stack.split('\n').slice(1); // Skip first line (error message)
	return lines
		.map((line) => {
			// Match patterns like: at FunctionName (file:line:column) or at file:line:column
			const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)|at\s+(.+?):(\d+):(\d+)/);
			if (match) {
				return {
					function: match[1] || undefined,
					file: match[2] || match[5] || undefined,
					line: parseInt(match[3] || match[6] || '0', 10) || undefined,
					column: parseInt(match[4] || match[7] || '0', 10) || undefined,
				};
			}
			return null;
		})
		.filter((item): item is NonNullable<typeof item> => item !== null)
		.slice(0, 5); // Limit to first 5 stack frames
};

export const logError = (label: string, errorInfo: ErrorInfo) => {
	console.error('Global Error Handler:', label, errorInfo);
};

/**
 * Log React Router route errors (called from RouteErrorHandler)
 * Also sends to Sentry
 */
export const logRunTimeError = (error: ErrorInfo) => {
	logError('Frontend Error', error);
	
	// Send to Sentry with full context
	const sentryError = new Error(error.message || 'Frontend runtime error');
	sentryError.name = error.name || 'Error';
	sentryError.stack = error.stack;
	const errorPathname = error.pathname || location.pathname;
  const moduleName = getModuleName(errorPathname);
	if (location.hostname !== 'localhost') {
		Sentry.captureException(sentryError, {
			tags: {
				issue_type: 'CODE_BASE',
				error_type: error.type,
				module: moduleName,
				user_agent: navigator.userAgent,
			},
			extra: {
				...error,
				pathname: error.pathname,
				href: error.href,
				search: error.search,
				hash: error.hash,
				stackTrace: error.stackTrace,
			},
		});
	}
};


